import React from 'react';
import { Button } from '../../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { Label } from '../../ui/label';
import { Plus, Trash2, MessageSquareQuote, Star } from 'lucide-react';
import { COMPANY } from '../../../config/company';

// Default Introduction Letter
const DEFAULT_INTRO_LETTER = `At JY Mega FC Construction Inc., we believe a roof replacement estimate is more than just a list of numbers—it's your roadmap to a safer, more resilient home in Florida's challenging climate. As a family-owned, fully licensed (CGC1536790), insured, and certified general contractor with over 20 years of experience, we're committed to turning potential vulnerabilities into strong, enduring solutions. This document isn't merely a quote; it's our promise of quality craftsmanship, code compliance, and personalized service tailored to protect your family and investment against hurricanes, heavy rains, and relentless sun.

Florida's unique environment demands expertise. From Hurricane Ian's aftermath to everyday wind events, roofs here must meet strict Florida Building Code (FBC) standards for high-velocity hurricane zones (HVHZ) in areas like Broward and Collier Counties. Our estimates incorporate these essentials: premium materials like impact-resistant TPO, modified bitumen, or tile systems; enhanced wind-uplift mitigation; and energy-efficient insulation to qualify for insurance discounts via wind mitigation credits. By choosing us, you're not just replacing a roof—you're enhancing your home's value, reducing energy bills, and gaining eligibility for up to 50% savings on windstorm premiums through proper documentation and inspections.

What sets JY Mega FC Construction apart? We're not a fly-by-night operation; we're a trusted team led by Jhony Alexander Ochoa, serving Naples, Fort Lauderdale, Tamarac, and beyond. Our 5-star reviews on Yelp, Angi, and Houzz speak volumes: "Outstanding workmanship... amazed by the results," says one client after a full remodel. We've handled everything from home additions and kitchen renovations to impact windows and post-storm repairs, ensuring every project is on-time, clean, and budget-conscious. We offer free consultations, transparent pricing with no hidden fees, and a satisfaction guarantee—because your peace of mind is our mission.

This estimate details every line item, from tear-off to final certification, using industry-standard tools like Xactimate for accuracy. It's designed to empower you: Compare it confidently, knowing it reflects superior quality, creative solutions, and a commitment to turning your dreams into reality. Don't settle for less in a state where storms don't compromise—choose JY Mega FC Construction for a roof that stands the test of time.

Ready to proceed? Contact us at 954-339-3278 or jymegaconstruction@gmail.com for your free site assessment. Let's build a stronger future together.

Sincerely,

Johny Pichardo,
CEO
JYMegaFcConstruction, Inc.`;

// Default Reviews Content
const DEFAULT_REVIEWS = `WHAT OUR CLIENTS SAY ABOUT US

⭐⭐⭐⭐⭐ From Angi (Church Project, including new roof):
"JY Mega Construction completed multiple projects at our church. They put on a new roof, repaired and painted our very old building and rebuilt the cross that had started to fall off of the steeple and had to be removed. Throughout each project, Jhony kept in contact with me the entire time."
— Satisfied Church Client, Naples Area

⭐⭐⭐⭐⭐ From Angi (General Excellence & Communication):
"We have used JY Mega for several projects in the last few months including a kitchen remodel. We were very impressed by the quality of their work. The workmanship was outstanding and we were amazed by the results. The entire team was great. They were knowledgeable, on time, and always cleaned up when they were done."
— Homeowner, Multiple Projects

⭐⭐⭐⭐⭐ From Angi (Ongoing Responsiveness):
"Jhonny and JY Mega Construction remained in continued communication, and provided stellar results for our project... Even after the work had completed they continued to remain in touch, and were immediately responsive to any requests no matter how large or small."
— Client on Outdoor/Remodel Project

⭐⭐⭐⭐⭐ From Yelp (Prompt & Competitive Service):
"We recently had JY Mega FC Construction install full impact windows/doors/garage door on our new home. From the beginning of the process Jhony and the team were prompt, thorough and extremely competitive with pricing. We couldn't be happier with the job they did."
— New Home Client

⭐⭐⭐⭐⭐ From HomeAdvisor (Knowledge & Reassurance):
"My home had water damage upstairs and downstairs. From the very beginning JY Mega FC Construction came by and I felt very reassured that they had very good knowledge of their work."
— Water Damage Repair Client

📊 Overall Ratings Summary:
• Angi: 4.7 out of 5 stars (based on 100+ reviews)
• Yelp: 5 stars (from verified clients)
• BuildZoom Score: 114 (above 98% of Florida contractors)
• Trusted for roofing, remodels, and storm-related work in South Florida`;

const EstimateDocumentSections = ({
  language,
  lead,
  settings,
  handleInputChange,
  // Misc items props
  addMiscItem,
  handleMiscItemChange,
  removeMiscItem
}) => {
  return (
    <>
      {/* Miscellaneous Items - Additional items at the bottom of estimate */}
      <Card className="border-2 border-teal-300" data-testid="misc-items-section">
        <CardHeader className="bg-teal-600 text-white rounded-t-lg">
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center">
              <Plus className="mr-2 h-5 w-5" />
              {language === 'es' ? 'Items Misceláneos' : 'Miscellaneous Items'}
            </span>
            <Button size="sm" variant="secondary" onClick={addMiscItem} data-testid="add-misc-item-btn">
              <Plus className="h-4 w-4 mr-1" /> {language === 'es' ? 'Agregar' : 'Add'}
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          <p className="text-sm text-teal-600 mb-4">
            {language === 'es' 
              ? 'Agregue items adicionales como permisos, tarifas administrativas, etc.' 
              : 'Add additional items like permits, admin fees, etc.'}
          </p>
          {(lead.misc_items || []).length === 0 ? (
            <p className="text-gray-400 text-center py-6">
              {language === 'es' ? 'Sin items adicionales' : 'No additional items'}
            </p>
          ) : (
            <div className="space-y-3">
              {(lead.misc_items || []).map((item) => (
                <div key={item.id} className="flex items-center gap-2 p-3 bg-teal-50 rounded-lg border border-teal-200">
                  <Input 
                    placeholder={language === 'es' ? 'Título (ej: Permiso de Construcción)' : 'Title (e.g., Building Permit)'} 
                    value={item.title || ''} 
                    onChange={(e) => handleMiscItemChange(item.id, 'title', e.target.value)}
                    className="flex-1"
                  />
                  <Input 
                    placeholder={language === 'es' ? 'Descripción' : 'Description'} 
                    value={item.description || ''} 
                    onChange={(e) => handleMiscItemChange(item.id, 'description', e.target.value)}
                    className="flex-1"
                  />
                  <div className="relative w-32">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                    <Input 
                      type="number" 
                      placeholder="0.00" 
                      value={item.price || ''} 
                      onChange={(e) => handleMiscItemChange(item.id, 'price', e.target.value)}
                      className="pl-7 text-right"
                      step="0.01"
                    />
                  </div>
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    onClick={() => removeMiscItem(item.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              {/* Misc Items Total */}
              <div className="flex justify-end pt-3 border-t border-teal-200">
                <div className="text-right">
                  <span className="text-sm text-teal-600">
                    {language === 'es' ? 'Subtotal Misceláneos:' : 'Misc Subtotal:'}
                  </span>
                  <span className="ml-2 font-bold text-teal-700">
                    ${(lead.misc_items || []).reduce((sum, item) => sum + (parseFloat(item.price) || 0), 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                  </span>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Contractor Overhead & Profit - Show for all estimate types */}
      <Card data-testid="overhead-profit-section">
        <CardContent className="pt-6 space-y-4">
          {/* Contractor Overhead */}
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm font-medium">
                {language === 'es' ? 'Gastos Generales del Contratista' : 'Contractor Overhead'}
              </Label>
              <p className="text-xs text-gray-500">
                {language === 'es' ? 'Gastos operativos (10%)' : 'Operating expenses (10%)'}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-600">10%</span>
              <label className="relative inline-flex items-center cursor-pointer">
                <input 
                  type="checkbox" 
                  checked={lead.apply_overhead === true}
                  onChange={(e) => handleInputChange('apply_overhead', e.target.checked)}
                  className="sr-only peer"
                  data-testid="overhead-toggle"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-500"></div>
              </label>
            </div>
          </div>
          
          {/* Contractor Profit */}
          <div className="flex items-center justify-between pt-2 border-t">
            <div>
              <Label className="text-sm font-medium">
                {language === 'es' ? 'Ganancia del Contratista' : 'Contractor Profit'}
              </Label>
              <p className="text-xs text-gray-500">
                {language === 'es' ? 'Margen de ganancia (10%)' : 'Profit margin (10%)'}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-600">10%</span>
              <label className="relative inline-flex items-center cursor-pointer">
                <input 
                  type="checkbox" 
                  checked={lead.apply_profit === true}
                  onChange={(e) => handleInputChange('apply_profit', e.target.checked)}
                  className="sr-only peer"
                  data-testid="profit-toggle"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500"></div>
              </label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Introduction Letter Section */}
      <Card data-testid="intro-letter-section">
        <CardHeader style={{ backgroundColor: COMPANY.primaryColor }} className="text-white rounded-t-lg">
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center">
              <MessageSquareQuote className="mr-2 h-5 w-5" />
              {language === 'es' ? 'Carta de Introducción' : 'Introduction Letter'}
            </span>
            <label className="flex items-center text-sm font-normal">
              <input 
                type="checkbox" 
                checked={lead.show_intro_letter !== false}
                onChange={(e) => handleInputChange('show_intro_letter', e.target.checked)}
                className="mr-2 h-4 w-4"
              />
              {language === 'es' ? 'Mostrar en PDF' : 'Show in PDF'}
            </label>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4 space-y-3">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <p className="text-sm font-medium text-amber-800">
              Dear {lead.client_name || '(Client Name)'},
            </p>
          </div>
          <Textarea 
            value={lead.introduction_letter || ''} 
            onChange={(e) => handleInputChange('introduction_letter', e.target.value)} 
            rows={8} 
            className="font-mono text-sm" 
            placeholder={language === 'es' ? 'Ingrese carta de introducción...' : 'Enter introduction letter...'}
          />
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => handleInputChange('introduction_letter', DEFAULT_INTRO_LETTER)}
            className="text-amber-600 border-amber-300"
          >
            {language === 'es' ? 'Usar Texto de Ejemplo' : 'Use Sample Text'}
          </Button>
          {settings.introductionLetter && !lead.introduction_letter && (
            <p className="text-xs text-green-600">
              ✓ {language === 'es' ? 'Usando carta de Configuración' : 'Using intro letter from Settings'}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Customer Reviews Section */}
      <Card data-testid="reviews-section">
        <CardHeader style={{ backgroundColor: COMPANY.primaryColor }} className="text-white rounded-t-lg">
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center">
              <Star className="mr-2 h-5 w-5" />
              {language === 'es' ? 'Reseñas de Clientes' : 'Customer Reviews'}
            </span>
            <label className="flex items-center text-sm font-normal">
              <input 
                type="checkbox" 
                checked={lead.show_reviews !== false}
                onChange={(e) => handleInputChange('show_reviews', e.target.checked)}
                className="mr-2 h-4 w-4"
              />
              {language === 'es' ? 'Mostrar en PDF' : 'Show in PDF'}
            </label>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4 space-y-3">
          <p className="text-xs text-gray-500">
            {language === 'es' 
              ? 'Deje en blanco para usar las reseñas de Configuración.'
              : 'Leave blank to use reviews from Settings.'}
          </p>
          <Textarea 
            value={lead.reviews_content || ''} 
            onChange={(e) => handleInputChange('reviews_content', e.target.value)} 
            rows={6} 
            className="font-mono text-sm" 
            placeholder={language === 'es' 
              ? (settings.reviews ? 'Usando reseñas de Configuración...' : 'Ingrese reseñas...')
              : (settings.reviews ? 'Using reviews from Settings...' : 'Enter customer reviews...')}
          />
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => handleInputChange('reviews_content', DEFAULT_REVIEWS)}
              className="text-amber-600 border-amber-300"
            >
              {language === 'es' ? 'Usar Texto de Ejemplo' : 'Use Sample Text'}
            </Button>
            {settings.reviews && !lead.reviews_content && (
              <p className="text-xs text-green-600">
                ✓ {language === 'es' ? 'Usando reseñas de Configuración' : 'Using reviews from Settings'}
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Terms & Conditions */}
      <Card data-testid="terms-section">
        <CardHeader style={{ backgroundColor: COMPANY.primaryColor }} className="text-white rounded-t-lg">
          <CardTitle>{language === 'es' ? 'Términos y Condiciones' : 'Terms & Conditions'}</CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <p className="text-xs text-gray-500 mb-2">
            {language === 'es' 
              ? 'Deje en blanco para usar los términos predeterminados de Configuración, o escriba términos personalizados para este cliente.'
              : 'Leave blank to use default terms from Settings, or enter custom terms for this client.'}
          </p>
          <Textarea 
            value={lead.terms_and_conditions || ''} 
            onChange={(e) => handleInputChange('terms_and_conditions', e.target.value)} 
            rows={8} 
            className="font-mono text-sm" 
            placeholder={language === 'es' 
              ? (settings.termsEs ? 'Usando términos de Configuración...' : 'Ingrese términos y condiciones...')
              : (settings.termsEn ? 'Using terms from Settings...' : 'Enter terms and conditions...')}
          />
          <div className="flex items-center gap-2 mt-2">
            {lead.terms_and_conditions && (settings.termsEn || settings.termsEs) && (
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => handleInputChange('terms_and_conditions', '')}
                className="text-blue-600 border-blue-300"
              >
                {language === 'es' ? 'Usar Términos de Configuración' : 'Use Settings Terms'}
              </Button>
            )}
            {(settings.termsEn || settings.termsEs) && !lead.terms_and_conditions && (
              <p className="text-xs text-green-600">
                ✓ {language === 'es' ? 'Usando términos de Configuración' : 'Using default terms from Settings'}
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    </>
  );
};

export default EstimateDocumentSections;
